import { Injectable } from '@angular/core';
import { EventName, Events } from '../utils/events';
import { Dataset, DatasetProvider, DatasetType } from './dataset';
import { API } from './api';
import { LocalStorageProvider } from './local-storage';
import { BaseProvider } from './base';
import { BehaviorSubject } from 'rxjs';

export class Project {
    id: string;
    name: string;
    datasetIdentifiers: string[] = [];
    previewImageUrls: string[];
    dateAdded: string;
    numPromotedBrains: number;
    exportable: boolean;
}

@Injectable()
export class ProjectProvider extends BaseProvider {
    public projectsSubscription: BehaviorSubject<Project[]> = new BehaviorSubject([]);
    private _projects: Project[] = [];

    get projects(): Project[] {
        return this._projects;
    }

    set projects(projects: Project[]) {
        this.setProjects(projects);
    }

    constructor(
        public datasetProvider: DatasetProvider,
        public api: API,
        private localStorageProvider: LocalStorageProvider,
        private events: Events
    ) {
        super();

        this.events.subscribe(EventName.DatasetProviderDelete, dataset => {
            this.processDatasetDeleteEvent(dataset);
        });
    }

    public getProjectNamePrefix(projectName: string): string {
        return projectName.toLowerCase().includes('project') ? '' : ' Project:';
    }

    public async processDatasetDeleteEvent(dataset: Dataset): Promise<void> {
        try {
            const project = this.getProjectById(dataset.projectId);
            if (project === undefined) {
                return;
            }

            project.datasetIdentifiers = project.datasetIdentifiers.filter(
                datasetIdentifier => datasetIdentifier != dataset.id
            );

            project.exportable = this.isExportable(project);
        } catch (err) {
            throw err;
        }
    }

    private async fetchProjects(): Promise<Project[]> {
        try {
            await this.ensureDependenciesLoaded([this.datasetProvider]);

            const response = await this.api.get('projects');

            this.projects = response.message.projects.map(obj => {
                return this.toProject(obj);
            });

            return this.projects;
        } catch (err) {
            throw err;
        }
    }

    public async fetchProjectsDebounce(): Promise<Dataset[]> {
        return this.debounce(
            'fetchProjects',
            async () => {
                return await this.fetchProjects();
            },
            250
        );
    }

    public async load(force: boolean = false): Promise<Project[]> {
        return await this.ensureLoaded(() => this.fetchProjectsDebounce(), force);
    }

    public async deactivateProject(id: string): Promise<void> {
        try {
            await this.api.delete(`projects/${id}`);

            // Update book keeping
            const index = this.projects.findIndex(project => project.id === id);
            if (this.projects.length > 1) {
                this.projects.splice(index, 1);
                // Must call setProjects specifically due to setters not being called on arrays in angular.
                this.setProjects(this.projects);
            } else {
                this.projects = [];
            }
        } catch (err) {
            throw err;
        }
    }

    /**
     * Determine if a project has polygons based on whether any child datasets have polygons
     */
    public hasPolygons(project: Project): boolean {
        for (const datasetIdentifier of project.datasetIdentifiers) {
            const dataset = this.datasetProvider.getDatasetById(datasetIdentifier);
            if (dataset !== undefined && dataset.hasPolygons()) {
                return true;
            }
        }

        return false;
    }

    /**
     * Stores the active image for this project/dataset so we can reload it later
     */
    public async storeActiveImageIdentifier(imageIdentifier: string) {
        try {
            await this.localStorageProvider.set(`activeAsset`, imageIdentifier);
        } catch (err) {
            throw err;
        }
    }

    /**
     * Load the active image for the current project/dataset if they exist
     */
    public async loadStoredActiveImageIdentifier(): Promise<string> {
        try {
            return await this.localStorageProvider.get(`activeAsset`);
        } catch (err) {
            throw err;
        }
    }

    /**
     * Remove the stored active image if there is one
     */
    public async removeStoredActiveImageIdentifier() {
        try {
            await this.localStorageProvider.remove(`activeAsset`);
        } catch (err) {
            throw err;
        }
    }

    /**
     * Find a project from an id
     */
    public getProjectById(id: string): Project | undefined {
        return this.projects.find(project => project.id === id);
    }

    public getCount(): number {
        return this.projects.length;
    }

    public getProjects(limit: number = this.projects.length): Project[] {
        return this.projects.slice(0, limit);
    }

    /**
     * Reload a project from BrainBank and replace the details of the local project object
     */
    public async reloadProject(id: string): Promise<Project> {
        try {
            const response = await this.api.get(`projects/${id}`);
            const project = this.toProject(response.message);

            const location = this.projects.findIndex(project => project.id === id);

            if (Number.isInteger(location)) {
                this.projects[location] = project;
            }

            return project;
        } catch (err) {
            throw err;
        }
    }

    /**
     * A common method to convert a BrainBank response object to a local Project object
     */
    public toProject(obj): Project {
        const project = new Project();
        project.id = obj.projectIdentifier;
        project.name = obj.name;
        project.previewImageUrls = obj.previewImageUrls || [];
        project.datasetIdentifiers = obj.datasetIdentifiers || [];
        project.dateAdded = obj.dateAdded;
        project.numPromotedBrains = obj.promotedBrainCount;
        project.exportable = this.isExportable(project);

        return project;
    }

    /**
     * Create a new project
     */
    public async createProject(name: string) {
        try {
            const response = await this.api.post('projects', { name });

            const project = new Project();
            project.id = response.message;
            project.name = name;
            project.previewImageUrls = [];
            project.dateAdded = new Date().toString();
            project.exportable = this.isExportable(project);

            this.projects.push(project);
            // Setter is not called on mutations (e.g.: push), thus we need
            // to explicitly set, so that BehaviorSubject changes.
            this.setProjects(this.projects);

            return project;
        } catch (err) {
            throw err;
        }
    }

    /**
     * Rename a project
     * @param newName
     * @param projectIdentifier
     */
    public async renameProject(newName: string, projectIdentifier: string) {
        try {
            await this.api.post(`projects/${projectIdentifier}/rename`, {
                name: newName
            });
        } catch (e) {
            throw e;
        }
    }

    public resetInfo(): void {
        this.projects = [];
        super.resetInfo();
    }

    private isExportable(project: Project): boolean {
        for (const datasetIdentifier of project.datasetIdentifiers) {
            const dataset = this.datasetProvider.getDatasetById(datasetIdentifier);
            if (dataset && (dataset.isSegmentation() || dataset.isObjectDetection())) {
                return true;
            }
        }

        return false;
    }

    private setProjects(projects: Project[] = []): void {
        this._projects = projects;
        this.projectsSubscription.next(this._projects);
    }
}



// WEBPACK FOOTER //
// ./src/providers/project.ts